import { Check, type LucideIcon } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { cn } from '@/utils/cn'
import { CHIP, MICRO_LABEL, MONO } from '../recipes'

export interface ToggleChipOption<T extends string> {
  value: T
  label: string
  /** Shown before the label while the chip is off; the Check takes its place once it is on. */
  Icon?: LucideIcon
  /** Shown, but not to be picked: the group holds as many as it takes. */
  disabled?: boolean
}

export interface ToggleChipGroup<T extends string> {
  legend: string
  options: readonly ToggleChipOption<T>[]
  /** Show this many options and a "+N" chip for the rest. */
  collapseAfter?: number
}

export interface ToggleChipsProps<T extends string> {
  group: ToggleChipGroup<T>
  selected: readonly T[]
  onToggle: (value: T) => void
  /**
   * `wrap` (default): a visible legend over chips that wrap. `strip`: a filter row under a
   * search field, with the legend for screen readers only; on the phone the chips stay on one
   * line that scrolls sideways and bleeds through the 16 px gutter, on the desktop they wrap.
   */
  layout?: 'wrap' | 'strip'
}

const LAYOUT = {
  wrap: { root: '', legend: MICRO_LABEL, group: 'flex-wrap' },
  strip: {
    root: '-mx-4 min-w-0 @5xl/humint:mx-0',
    legend: 'sr-only',
    group:
      'flex-nowrap overflow-x-auto px-4 pb-1 [scrollbar-width:none] @5xl/humint:flex-wrap @5xl/humint:px-0',
  },
} as const

/** Adds or removes a value: the one toggle helper of multi-select chips and filters. */
export function toggleValue<T>(list: readonly T[], value: T): T[] {
  return list.includes(value) ? list.filter(v => v !== value) : [...list, value]
}

/**
 * A labelled set of multi-select chips on a Radix ToggleGroup, so arrow keys move between them
 * (selected = copper border + wash + Check icon + weight). With `collapseAfter`, a "+N" chip
 * shows the rest; selected options always stay visible. See `layout` for the filter-strip form.
 */
export function ToggleChips<T extends string>({
  group,
  selected,
  onToggle,
  layout = 'wrap',
}: ToggleChipsProps<T>) {
  const legendId = useId()
  const classes = LAYOUT[layout]
  const [expanded, setExpanded] = useState(false)
  const limit = group.collapseAfter ?? group.options.length
  const visible = expanded
    ? group.options
    : group.options.filter((o, i) => i < limit || selected.includes(o.value))
  const hiddenCount = group.options.length - visible.length
  const onValueChange = (next: string[]) => {
    const changed = visible.find(o => next.includes(o.value) !== selected.includes(o.value))
    if (changed) onToggle(changed.value)
  }
  return (
    <div className={cn('flex flex-col gap-2', classes.root)}>
      <p id={legendId} className={classes.legend}>
        {group.legend}
      </p>
      <ToggleGroup
        type="multiple"
        spacing={2}
        value={[...selected]}
        aria-labelledby={legendId}
        onValueChange={onValueChange}
        className={cn('w-full', classes.group)}
      >
        {visible.map(option => (
          <Chip key={option.value} option={option} on={selected.includes(option.value)} />
        ))}
        {(hiddenCount > 0 || expanded) && limit < group.options.length && (
          <MoreChip
            expanded={expanded}
            hiddenCount={hiddenCount}
            onToggle={() => setExpanded(v => !v)}
          />
        )}
      </ToggleGroup>
    </div>
  )
}

/** One chip of the group: its own icon gives way to the Check once it is on. */
function Chip<T extends string>({ option, on }: { option: ToggleChipOption<T>; on: boolean }) {
  const Icon = on ? Check : option.Icon
  return (
    <ToggleGroupItem
      value={option.value}
      disabled={option.disabled}
      className={cn(CHIP, 'min-w-0 hover:text-foreground')}
    >
      {Icon && <Icon aria-hidden className={cn('size-4', on && 'text-primary-strong')} />}
      {option.label}
    </ToggleGroupItem>
  )
}

interface MoreChipProps {
  expanded: boolean
  hiddenCount: number
  onToggle: () => void
}

/** "+N" to show the collapsed options, "Fewer" to fold them again. Not a toggle of the group. */
function MoreChip({ expanded, hiddenCount, onToggle }: MoreChipProps) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      aria-expanded={expanded}
      onClick={onToggle}
      className={cn(CHIP, 'border-dashed text-muted-foreground')}
    >
      {expanded ? (
        t('humint.common.chips.fewer')
      ) : (
        <span className={MONO}>{t('humint.common.chips.more', { count: hiddenCount })}</span>
      )}
    </button>
  )
}
